// Export invoice (15.1, plan task 7). Mirrors backend com.kervolt.oms.commercial.dataclass.
// {ExportInvoiceDTO,ExportInvoiceLineDTO,CreateExportInvoiceRequest,UpdateExportInvoiceRequest,
// ExportInvoiceLineRequest} verbatim (R-15 house convention: module DTOs live in their own file).

export type Incoterm = 'FOB' | 'CIF' | 'CFR' | 'EXW' | 'FCA' | 'DAP' | 'DDP';

export const INCOTERMS: { value: Incoterm; label: string }[] = [
    { value: 'FOB', label: 'FOB — Free On Board' },
    { value: 'CIF', label: 'CIF — Cost, Insurance & Freight' },
    { value: 'CFR', label: 'CFR — Cost & Freight' },
    { value: 'EXW', label: 'EXW — Ex Works' },
    { value: 'FCA', label: 'FCA — Free Carrier' },
    { value: 'DAP', label: 'DAP — Delivered At Place' },
    { value: 'DDP', label: 'DDP — Delivered Duty Paid' },
];

/** Export invoice header detail (backend ExportInvoiceDTO). `invoiceNumber` is server-assigned
 * and immutable. `totalQuantity`/`totalAmount` are computed roll-ups, recomputed from live lines
 * on every line write — never settable through the create/update request. Lines are NOT embedded
 * here — fetched separately via `listLines`, mirroring `PackingListDTO`/carton lines. */
export interface IExportInvoice {
    id: string;
    institutionId: string;
    invoiceNumber: string;
    invoiceDate: string;
    buyerOrderId: string;
    packingListId?: string;
    billOfLadingId?: string;
    exportLcId?: string;
    exporterBankDetails?: string;
    consigneeName?: string;
    consigneeAddress?: string;
    notifyPartyName?: string;
    notifyPartyAddress?: string;
    lcNumber?: string;
    lcDate?: string;
    issuingBankName?: string;
    issuingBankPartyId?: string;
    incoterm?: Incoterm;
    incotermPlace?: string;
    portOfLoading?: string;
    portOfDischarge?: string;
    finalDestination?: string;
    countryOfOrigin?: string;
    currencyCode?: string;
    totalQuantity: number;
    totalAmount: number;
    remarks?: string;
}

/** One export invoice line (backend ExportInvoiceLineDTO). `lineAmount` is a server-computed
 * roll-up (`quantity * unitPrice`) — never settable through the request. */
export interface IExportInvoiceLine {
    id: string;
    exportInvoiceId: string;
    styleId?: string;
    description: string;
    hsCode?: string;
    colorName?: string;
    sizeName?: string;
    quantity: number;
    uom?: string;
    unitPrice: number;
    lineAmount: number;
    sortOrder: number;
}

/** Body of `POST institution/{institutionId}` -- create = prefill from a buyer order. No
 * `invoiceNumber` field -- server-assigned. `incoterm` is optional here: when omitted the service
 * falls back to the buyer order's free-text `incoterm`. */
export interface ICreateExportInvoiceRequest {
    buyerOrderId: string;
    invoiceDate: string;
    packingListId?: string;
    billOfLadingId?: string;
    exportLcId?: string;
    incoterm?: Incoterm;
    incotermPlace?: string;
    portOfLoading?: string;
    portOfDischarge?: string;
    finalDestination?: string;
    countryOfOrigin?: string;
    consigneeName?: string;
    consigneeAddress?: string;
    notifyPartyName?: string;
    notifyPartyAddress?: string;
    exporterBankDetails?: string;
    remarks?: string;
}

/** Body of `PUT institution/{institutionId}/{id}`. Deliberately carries NO `invoiceNumber` and NO
 * `buyerOrderId` -- both are immutable after create. `exporterBankDetails` MUST be round-tripped
 * on every save -- omitting it wipes the frozen block. `incoterm`/`currencyCode` are required. */
export interface IUpdateExportInvoiceRequest {
    invoiceDate: string;
    packingListId?: string;
    billOfLadingId?: string;
    exportLcId?: string;
    exporterBankDetails?: string;
    consigneeName?: string;
    consigneeAddress?: string;
    notifyPartyName?: string;
    notifyPartyAddress?: string;
    lcNumber?: string;
    lcDate?: string;
    issuingBankName?: string;
    issuingBankPartyId?: string;
    incoterm: Incoterm;
    incotermPlace?: string;
    portOfLoading?: string;
    portOfDischarge?: string;
    finalDestination?: string;
    countryOfOrigin?: string;
    currencyCode: string;
    remarks?: string;
}

/** One line of a `PUT {id}/lines` replace-all payload (backend ExportInvoiceLineRequest). `id`,
 * when present, matches an existing LIVE line to UPDATE IN PLACE; omitted means INSERT; a live
 * line whose id is absent from the whole payload is soft-deleted. No `lineAmount` field -- always
 * server-computed. No `sortOrder` field -- always assigned from the payload's LIST INDEX. */
export interface IExportInvoiceLineRequest {
    id?: string;
    styleId?: string;
    description: string;
    hsCode?: string;
    colorName?: string;
    sizeName?: string;
    quantity: number;
    uom?: string;
    unitPrice: number;
}

/** Body of `PUT {id}/lines` (replace-all). An empty array is allowed -- clearing the whole line
 * set to re-key it is a supported flow. */
export interface IReplaceExportInvoiceLinesRequest {
    lines: IExportInvoiceLineRequest[];
}
